import { SelectionPromotion } from '@teko/cart/v2';
import {
  CheckOutlined,
  DownOutlined,
  GiftFilled,
  UpOutlined,
} from '@teko/ds-icon';
import {
  Body,
  Caption,
  Divider,
  Flex,
  Row,
  ThemeProps,
  styled,
} from '@teko/ds-mobile';
import { trackManually } from '@teko/tracking';
import { DividerStyleEnum } from '@teko/types/dist/shared';
import { requestIdleCallback } from '@teko/utils';
import { useProductDetailState } from 'contexts';
import { t } from 'i18n';
import { useState } from 'react';

interface ISelectionPromotionsProps {
  title: string;
  description?: string;
  promotions?: SelectionPromotion[];
}

const MAX_ITEMS_PROMOTIONS = 3;

export const SelectionPromotions = (props: ISelectionPromotionsProps) => {
  const { title, description, promotions = [] } = props;

  const { product, setSelectedPromotion } = useProductDetailState();
  const [showFullPromotions, setShowFullPromotions] = useState(false);

  const handlePromotionClick = (
    promotion: SelectionPromotion,
    index: number,
  ) => {
    if (!promotion) return;
    if (promotion.isSelected) {
      setSelectedPromotion?.(undefined);
    } else {
      setSelectedPromotion?.(promotion);
    }
    requestIdleCallback(() =>
      trackManually(
        promotion.name as string,
        'optionalPromotion',
        'productDetail',
        JSON.stringify({
          sku: product?.sku,
          action: promotion.isSelected ? 'unselect' : 'select',
          promotionID: promotion.promotionId,
          screenName: 'productDetail',
          index,
        }),
      ),
    );
  };

  const toggleShowFullPromotions = () => {
    setShowFullPromotions((prev) => !prev);
  };

  if (promotions?.length === 0) return null;

  const convertPromotion = showFullPromotions
    ? promotions
    : promotions.slice(0, MAX_ITEMS_PROMOTIONS);

  return (
    <Wrapper>
      <Caption level={2} color="#000000A6">
        {t(title)}:{`${description ? ` (${description})` : ``}`}
      </Caption>
      {convertPromotion?.map((promotion, index) => (
        <PromotionOption
          key={promotion.promotionId || index}
          promotion={promotion}
          onClick={() => handlePromotionClick(promotion, index)}
        />
      ))}
      {promotions.length > MAX_ITEMS_PROMOTIONS && (
        <StyledSeeMoreView>
          <Flex direction="row" onClick={toggleShowFullPromotions}>
            <PromotionOptionLabel level={1} color="textTitle">
              {showFullPromotions
                ? t('Ẩn bớt')
                : t('Xem thêm {{numOfPromotion}} KM khác', {
                    numOfPromotion: promotions.length - MAX_ITEMS_PROMOTIONS,
                  })}
            </PromotionOptionLabel>
            {showFullPromotions ? (
              <UpOutlined color="#000000E0" size={20} />
            ) : (
              <DownOutlined color="#000000E0" size={20} />
            )}
          </Flex>
        </StyledSeeMoreView>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 8px;
  background: linear-gradient(
    180deg,
    #f5f5f5 0%,
    rgba(245, 245, 245, 0.6) 100%
  );
  border-radius: 4px;
`;

const StyledSeeMoreView = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 14px;
`;

export interface PromotionOptionProps {
  promotion: SelectionPromotion;
  onClick?: () => void;
}

const PromotionOption = ({ promotion, onClick }: PromotionOptionProps) => {
  const { isSelectable, isSelected } = promotion;

  return (
    <PromotionOptionWrapper
      onClick={!!isSelectable ? onClick : undefined}
      isSelected={!!isSelected}
    >
      <Row align="middle" justify="start" width="100%" padding={['s4', 's8']}>
        <GiftFilled color="secondary700" size={16} />
        <PromotionOptionLabel level={1} color="textTitle" marginLeft="s8">
          {promotion.title}
        </PromotionOptionLabel>
      </Row>
      <Divider
        dividerStyle={DividerStyleEnum.dashed}
        color="#0000000F"
        gap="0"
        border="1px"
      />
      <Flex
        justify="space-between"
        width="100%"
        align="end"
        padding={['s4', 's8']}
      >
        <Flex direction="column" gap="s4">
          {!!promotion.highlightedDescriptions?.length && (
            <Caption level={1} weight="regular" color="pending700">
              {promotion.highlightedDescriptions[0]}
            </Caption>
          )}
          <ConditionText level={1} color="textPrimary">
            {promotion.description}
          </ConditionText>
          <ExpiredAt level={1} color="textPrimary">
            {`${t('HSD')}: ${new Date(
              promotion.endedAt ?? new Date(),
            ).toLocaleDateString('vi')}`}
          </ExpiredAt>
        </Flex>
        <Flex direction="column" width="min-content">
          <StyledTick isSelected={!!isSelected}>
            <CheckOutlined color="white" size={16} />
          </StyledTick>
        </Flex>
      </Flex>
    </PromotionOptionWrapper>
  );
};

const maxLineCss = (maxLines: number) => `
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${maxLines};
`;

export interface StyledProps extends ThemeProps {
  isSelected?: boolean;
}

const PromotionOptionWrapper = styled.div(
  ({ isSelected, theme }: StyledProps) => `
  margin-top: 8px;
  padding: 4px;
  background-color: white;
  width: 100%;
  border-radius: 8px;
  border: 1.5px solid
    ${isSelected ? theme?.colors.primary : '#0000000F'};
  cursor: pointer;
`,
);

const PromotionOptionLabel = styled(Body)`
  ${maxLineCss(3)}
  white-space: pre-line;
`;

const ConditionText = styled(Caption)`
  ${maxLineCss(2)}
`;

const ExpiredAt = styled(Caption)``;

const StyledTick = styled.div<{ isSelected?: boolean }>(
  ({ isSelected, theme }) => `
    width: 24px;
    height: 24px;
    border-radius: 999px;
    background-color: ${isSelected ? theme?.colors.primary500 : 'white'};
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${isSelected ? theme?.colors.primary : '#00000026'};

`,
);
